/**
 * PDF：用 pdfjs-dist 读页数、渲染页面（按需加载，只有预览 / 布置 PDF 时才下载 pdf.js）。
 */
import type { PDFDocumentProxy } from "pdfjs-dist";
import { assetUrl } from "../../core/api";

type PdfJs = typeof import("pdfjs-dist");

let lib: Promise<PdfJs> | null = null;

export function loadPdfJs(): Promise<PdfJs> {
  if (!lib) {
    lib = Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")])
      .then(([pdfjs, worker]) => {
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
        return pdfjs;
      })
      .catch((e: unknown) => {
        lib = null;
        throw e;
      });
  }
  return lib;
}

/** 打开 PDF（url 或本地文件内容）。用完调用 doc.loadingTask.destroy() */
export async function openPdf(src: string | ArrayBuffer): Promise<PDFDocumentProxy> {
  const pdfjs = await loadPdfJs();
  const task = typeof src === "string" ? pdfjs.getDocument({ url: src }) : pdfjs.getDocument({ data: new Uint8Array(src) });
  return task.promise;
}

/** 附件的下载地址（builtin: 资源网页版没有） */
export async function pdfUrl(assetId: string): Promise<string> {
  const url = await assetUrl(assetId);
  if (!url) throw new Error("网页版不能打开内置资源");
  return url;
}

const pageCounts = new Map<string, number>();

/** PDF 附件的页数（缓存） */
export async function pdfPageCount(assetId: string): Promise<number> {
  const hit = pageCounts.get(assetId);
  if (hit != null) return hit;
  const doc = await openPdf(await pdfUrl(assetId));
  try {
    pageCounts.set(assetId, doc.numPages);
    return doc.numPages;
  } finally {
    void doc.loadingTask.destroy();
  }
}

/** 本地文件的页数（选好文件后显示"共 N 页"）；不是有效的 PDF 返回 null */
export async function localPdfPageCount(file: Blob): Promise<number | null> {
  try {
    const doc = await openPdf(await file.arrayBuffer());
    const n = doc.numPages;
    void doc.loadingTask.destroy();
    return n;
  } catch {
    return null;
  }
}

/** 记住刚上传的 PDF 的页数，布置时不用再下载一遍 */
export function rememberPageCount(assetId: string, n: number): void {
  pageCounts.set(assetId, n);
}

/**
 * 把第 pageNo 页画到 canvas 上，宽度适配 cssWidth（按设备像素比放大保证清晰）。
 * 同一个 canvas 不能同时渲染两次：onTask 拿到取消函数，换页前先取消上一次。
 */
export async function renderPage(doc: PDFDocumentProxy, pageNo: number, canvas: HTMLCanvasElement, cssWidth: number, onTask?: (cancel: () => void) => void): Promise<void> {
  const page = await doc.getPage(pageNo);
  const base = page.getViewport({ scale: 1 });
  const scale = cssWidth / base.width;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const viewport = page.getViewport({ scale: scale * dpr });
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
  canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;
  const task = page.render({ canvas, viewport });
  onTask?.(() => task.cancel());
  await task.promise;
}
